import React, { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useGetLocalTransportDetailQuery } from "store/services/localTransportApi";
import { useAuth } from "hooks";
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Table,
    TableBody,
    TableCell,
    TableRow,
} from "@mui/material";
import { Print, ArrowDropDown, Image, PictureAsPdf } from "@mui/icons-material";
import moment from "moment";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import { downloadReceipt } from "utils/downloadUtils";

const LocalTransportPrintCard = () => {
    const { id } = useParams();
    const { data: row, isLoading } = useGetLocalTransportDetailQuery(id);
    const { loggedInUser } = useAuth();
    const componentRef = useRef();

    const getBranches = (row) => {
        const branches = Array.isArray(row?.branch)
            ? row.branch
            : row?.branch
                ? [row.branch]
                : [];
        return branches.map((b) => b?.name || "").filter(Boolean).join(", ");
    };

    if (isLoading)
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );

    if (!row)
        return (
            <Box p={3}>
                <Typography>No Data Found</Typography>
            </Box>
        );

    const breadcrumbs = [
        { title: "Local Transport Request", path: "/app/crm/local-transport/list" },
        { title: "Download/Print" },
    ];

    return (
        <BreadcrumbLayout
            breadcrumbs={breadcrumbs}
            headerActions={<PrintActions componentRef={componentRef} row={row} />}
        >
            <BreadcrumbLayout.Paper>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", p: { xs: 2, sm: 3 } }}>
                    <Card
                        ref={componentRef}
                        className="receipt-card"
                        sx={{
                            width: "100%",
                            maxWidth: "800px",
                            boxShadow: 2,
                            bgcolor: "#fff",
                            mt: 2,
                            border: "2px solid #333",
                        }}
                    >

                        {/* Header */}
                        <Box sx={{ p: 2.5, borderBottom: "3px solid #333" }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    {row.entity?.logo?.url ? (
                                        <Box
                                            component="img"
                                            src={row.entity.logo.url}
                                            alt={row.entity?.name || "Logo"}
                                            referrerPolicy="no-referrer"
                                            className="entity-logo"
                                            sx={{
                                                width: 60,
                                                height: 60,
                                                objectFit: "contain",
                                                border: "2px solid #333",
                                                p: 0.5,
                                            }}
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                            }}
                                        />
                                    ) : (
                                        <Box sx={{ width: 60, height: 60, border: "2px solid #333", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "2rem" }}>
                                            🏢
                                        </Box>
                                    )}
                                    <Box>
                                        <Typography variant="h5" fontWeight="800" sx={{ fontSize: "1.4rem", mb: 0.3 }}>
                                            {row.entity?.name || "N/A"}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontSize: "0.85rem", color: "#555" }}>
                                            Local Transport Expenses
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box>
                                    <Typography variant="h5" fontWeight="800" sx={{ fontFamily: "monospace", fontSize: "1.3rem" }}>
                                        {row.ltTag || `LT-${row._id?.slice(-6).toUpperCase()}`}
                                    </Typography>


                                </Box>

                            </Box>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>

                                <Typography variant="body1" fontWeight="700" sx={{ fontSize: "0.9rem", mt: 0.3 }}>
                                    Approved By : {row?.approvedBy?.name}
                                </Typography>
                                <Typography variant="body2" fontWeight="700" sx={{ fontSize: "0.9rem", justifySelf: "flex-end" }}>
                                    Status : {row?.status || "Pending"}
                                </Typography>
                            </Box>
                        </Box>

                        <CardContent sx={{ p: 2.5 }}>
                            {/* Location Section */}
                            <Box sx={{ mb: 3, p: 2, border: "2px solid #333", bgcolor: "#fafafa" }}>

                                {row.toBranch ? (
                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                        <Box>
                                            <Typography variant="caption" sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#666" }}>Source Branch</Typography>
                                            <Typography variant="h6" fontWeight="700" sx={{ fontSize: "1rem", mt: 0.3 }}>
                                                {getBranches(row) || "N/A"}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ fontSize: "2rem", fontWeight: 700 }}>→</Box>
                                        <Box sx={{ justifySelf: "flex-end" }}>
                                            <Typography variant="caption" sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#666" }}>Destination Branch</Typography>
                                            <Typography variant="h6" fontWeight="700" sx={{ fontSize: "1rem", mt: 0.3 }}>
                                                {row.toBranch?.name || "N/A"}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ) : (
                                    <Box>
                                        <Typography variant="caption" sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#666" }}>Source Branch</Typography>
                                        <Typography variant="h6" fontWeight="700" sx={{ fontSize: "1rem", mt: 0.3 }}>
                                            {getBranches(row) || "N/A"}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>

                            {/* Transport Info Table */}
                            <Box sx={{ mb: 3 }}>
                                {/* <Typography variant="caption" sx={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", mb: 1, display: "block", letterSpacing: "1px" }}>
                                    ▼ TRANSPORT INFORMATION
                                </Typography> */}
                                <Table sx={{ border: "2px solid #333" }}>
                                    <TableBody>
                                        <DataRow label="Transportation Mode" value={row.transportationMode?.name || "N/A"} />
                                        <DataRow label="Distance" value={`${row.distanceKm || 0} km`} />
                                        {row.noOfSacks && <DataRow label="Number of Sacks" value={row.noOfSacks} />}
                                        {row.vehicleId && (
                                            <DataRow
                                                label="Vehicle"
                                                value={`${row.vehicleId.vehicle_id || "N/A"} - Reg: ${row.vehicleId.registration_number || "N/A"}`}
                                            />
                                        )}
                                        {row.contractorId && (
                                            <DataRow label="Contractor" value={`${row.contractorId.name} (${row.contractorId.contractorId})`} />
                                        )}
                                        <DataRow label="Actual Cost" value={row.estimatedCost || "N/A"} />
                                        {row.actualCost && <DataRow label="Actual Cost" value={row.estimatedCost} />}
                                    </TableBody>
                                </Table>
                            </Box>

                            {/* Shipment Items */}
                            {row.shipmentId && row.shipmentId.length > 0 && (
                                <Box sx={{ mb: 2 }}>
                                    {/* <Typography variant="caption" sx={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", mb: 1, display: "block", letterSpacing: "1px" }}>
                                        ▼ ITEMS TO PICK ({row.shipmentId.length})
                                    </Typography> */}
                                    <Table sx={{ border: "2px solid #333" }}>
                                        <TableBody>
                                            <TableRow sx={{ bgcolor: "#333" }}>
                                                <TableCell sx={{ color: "#fff", fontWeight: 700, fontSize: "0.75rem", py: 1 }}>#</TableCell>
                                                <TableCell sx={{ color: "#fff", fontWeight: 700, fontSize: "0.75rem", py: 1 }}>SHIPMENT ID</TableCell>
                                                <TableCell sx={{ color: "#fff", fontWeight: 700, fontSize: "0.75rem", py: 1 }}>PICKED</TableCell>
                                            </TableRow>
                                            {row.shipmentId.map((shipment, idx) => (
                                                <TableRow key={idx} sx={{ "&:nth-of-type(even)": { bgcolor: "#f9f9f9" } }}>
                                                    <TableCell sx={{ fontSize: "0.8rem", fontWeight: 600, py: 1.2, border: "1px solid #ddd" }}>
                                                        {idx + 1}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: "0.85rem", fontWeight: 700, fontFamily: "monospace", py: 1.2, border: "1px solid #ddd" }}>
                                                        {shipment.shipmentId}
                                                    </TableCell>
                                                    <TableCell sx={{ py: 1.2, border: "1px solid #ddd" }}>
                                                        <Box sx={{ width: "20px", height: "20px", border: "2px solid #333" }} />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Box>
                            )}

                            {/* Remarks */}
                            {row.remarks && (
                                <Box sx={{ mb: 3, p: 2, border: "2px solid #333" }}>
                                    <Typography variant="caption" sx={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", mb: 1, display: "block" }}>
                                        Remarks
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                                        {row.remarks}
                                    </Typography>
                                </Box>
                            )}

                            {/* Signatures */}
                            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2, pt: 3, borderTop: "2px dashed #333" }}>
                                <Box>
                                    <Box sx={{ mt: 5, borderBottom: "1px solid #333" }}></Box>
                                    <Typography variant="caption" sx={{ fontSize: "0.6rem", color: "#888", mt: 0.5, display: "block", textAlign: "center" }}>
                                        PICKED BY
                                    </Typography>
                                </Box>
                                <Box>
                                    <Box sx={{ mt: 5, borderBottom: "1px solid #333" }}></Box>
                                    <Typography variant="caption" sx={{ fontSize: "0.6rem", color: "#888", mt: 0.5, display: "block", textAlign: "center" }}>
                                        LOADED BY

                                    </Typography>
                                </Box>
                                <Box>
                                    <Box sx={{ mt: 5, borderBottom: "1px solid #333" }}></Box>
                                    <Typography variant="caption" sx={{ fontSize: "0.6rem", color: "#888", mt: 0.5, display: "block", textAlign: "center" }}>
                                        RECEIVED BY
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Footer */}
                            <Box sx={{ mt: 3, pt: 2, borderTop: "1px solid #ddd", display: "flex", justifyContent: "space-between", fontSize: "0.7rem" }}>
                                <Typography variant="caption" sx={{ fontSize: "0.65rem", color: "#888" }}>
                                    Printed by: {loggedInUser?.name || "Unknown"} | {moment().format("DD/MM/YYYY HH:mm")}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </BreadcrumbLayout.Paper>

            <style>
                {`
                    @media print {
                        body * { visibility: hidden; }
                        .receipt-card, .receipt-card * { visibility: visible; }
                        .receipt-card {
                            position: absolute;
                            left: 50%;
                            top: 0;
                            transform: translateX(-50%) !important;
                            width: 800px !important;
                            max-width: 800px !important;
                            box-shadow: none !important;
                            margin: 0 !important;
                        }
                    }
                `}
            </style>
        </BreadcrumbLayout>
    );
};

const DataRow = ({ label, value, highlight }) => (
    <TableRow sx={{ bgcolor: highlight ? "#fff9e6" : "inherit" }}>
        <TableCell sx={{ fontSize: "0.75rem", fontWeight: 600, width: "40%", py: 1, border: "1px solid #ddd" }}>
            {label}
        </TableCell>
        <TableCell sx={{ fontSize: "0.8rem", fontWeight: 700, py: 1, border: "1px solid #ddd" }}>
            {value}
        </TableCell>
    </TableRow>
);

const PrintActions = ({ componentRef, row }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [downloading, setDownloading] = useState(false);

    const handlePrint = () => {
        window.print();
    };

    const handleDownloadClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleDownloadClose = () => {
        setAnchorEl(null);
    };

    const handleDownload = async (format) => {
        setDownloading(true);
        handleDownloadClose();

        try {
            const filename = `LT-${row.ltTag || row._id?.slice(-6)}`;
            await downloadReceipt(componentRef.current, filename, format);
        } catch (error) {
            console.error("Download failed:", error);
            alert(`Failed to download as ${format.toUpperCase()}`);
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="outlined" startIcon={<Print />} onClick={handlePrint} size="small">
                Print
            </Button>
            <Button
                variant="contained"
                endIcon={<ArrowDropDown />}
                onClick={handleDownloadClick}
                size="small"
                disabled={downloading}
            >
                {downloading ? "Downloading..." : "Download"}
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleDownloadClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
                <MenuItem onClick={() => handleDownload("pdf")}>
                    <ListItemIcon>
                        <PictureAsPdf fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Download as PDF</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleDownload("png")}>
                    <ListItemIcon>
                        <Image fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Download as PNG</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleDownload("jpeg")}>
                    <ListItemIcon>
                        <Image fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Download as JPEG</ListItemText>
                </MenuItem>
            </Menu>
        </Box>
    );
};

export default LocalTransportPrintCard;